import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, HttpError } from '../api/client';
import type { OrgRole, StoreGroup } from '../api/types';
import Toast, { type ToastMsg } from './Toast';

function errDetail(err: unknown): string {
  return err instanceof HttpError ? err.detail : 'unknown error';
}

function toggleId(ids: string[], id: string): string[] {
  return ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id];
}

/** One group row's inline-rename text lives in its own state — hooks can't
 * run inside a .map() callback, so the editable row is its own component. */
function GroupRow({
  group, onRename, onDelete,
}: {
  group: StoreGroup;
  onRename: (name: string) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(group.name);

  function commit() {
    const trimmed = name.trim();
    if (trimmed && trimmed !== group.name) onRename(trimmed);
    else setName(group.name);
  }

  return (
    <div className="flex items-center gap-3 py-3" data-testid={`group-row-${group.id}`}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        className="ff-input-sm flex-1 rounded-lg"
      />
      {/* onMouseDown preventDefault: keeps the input focused so a click here
          commits via onClick only, not a blur-then-click double-fire. */}
      <button
        type="button" data-testid={`rename-group-${group.id}`}
        onMouseDown={(e) => e.preventDefault()} onClick={commit}
        className="ff-btn-ghost px-3 py-1.5 text-xs"
      >
        Rename
      </button>
      <button
        type="button" data-testid={`delete-group-${group.id}`}
        onClick={() => {
          if (window.confirm(`Delete "${group.name}"? This only works once no stores remain in it; members lose the group.`)) onDelete();
        }}
        className="ff-btn-danger px-3 py-1.5 text-xs"
      >
        Delete
      </button>
    </div>
  );
}

export default function OrgManagePanel({ orgId }: { orgId: string }) {
  const qc = useQueryClient();
  const [tab, setTab] = useState<'groups' | 'people'>('groups');
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const detailQ = useQuery({ queryKey: ['org', orgId], queryFn: () => api.getOrg(orgId) });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['org', orgId] });
    qc.invalidateQueries({ queryKey: ['stores'] });
    qc.invalidateQueries({ queryKey: ['orgs'] });
  };
  const onErr = (err: unknown) => setToast({ kind: 'error', msg: errDetail(err) });

  // --- groups ---
  const [newGroup, setNewGroup] = useState('');
  const createGroup = useMutation({
    mutationFn: () => api.createGroup(orgId, { name: newGroup.trim() }),
    onSuccess: () => { setNewGroup(''); refresh(); },
    onError: onErr,
  });
  const renameGroup = useMutation({
    mutationFn: (v: { gid: string; name: string }) => api.renameGroup(orgId, v.gid, { name: v.name }),
    onSuccess: refresh,
    onError: onErr,
  });
  const deleteGroup = useMutation({
    mutationFn: (gid: string) => api.deleteGroup(orgId, gid),
    onSuccess: refresh,
    onError: onErr,
  });
  const moveStore = useMutation({
    mutationFn: (v: { storeId: string; gid: string | null }) => api.assignStoreToOrg(orgId, v.storeId, { group_id: v.gid }),
    onSuccess: refresh,
    onError: onErr,
  });

  // --- people ---
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<OrgRole>('member');
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [token, setToken] = useState<string | null>(null);
  const invite = useMutation({
    mutationFn: () => api.inviteOrgUser(orgId, { email: email.trim(), org_role: role, group_ids: groupIds }),
    onSuccess: (res) => { setEmail(''); setGroupIds([]); setToken(res.set_password_token); refresh(); },
    onError: onErr,
  });
  const updateUser = useMutation({
    mutationFn: (v: { uid: string; body: { org_role?: OrgRole; group_ids?: string[] } }) => api.updateOrgUser(orgId, v.uid, v.body),
    onSuccess: refresh,
    onError: onErr,
  });
  const toggleActive = useMutation({
    mutationFn: (v: { uid: string; active: boolean }) => api.setOrgUserActive(orgId, v.uid, v.active),
    onSuccess: refresh,
    onError: onErr,
  });

  const groups = detailQ.data?.org.groups ?? [];
  const stores = detailQ.data?.stores ?? [];
  const users = detailQ.data?.users ?? [];
  // Token rides in the URL FRAGMENT (#token=...) — never sent to a server/proxy log.
  const setPasswordUrl = token
    ? `${window.location.origin}/set-password#token=${encodeURIComponent(token)}`
    : null;

  return (
    <div className="space-y-6">
      <div className="ff-segment inline-flex gap-1" aria-label="Manage organization">
        <button
          type="button" data-testid="org-tab-groups" aria-pressed={tab === 'groups'}
          onClick={() => setTab('groups')}
          className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === 'groups' ? 'ff-segment-active' : 'text-ff-mute hover:text-ff-ink'}`}
        >
          Groups
        </button>
        <button
          type="button" data-testid="org-tab-people" aria-pressed={tab === 'people'}
          onClick={() => setTab('people')}
          className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${tab === 'people' ? 'ff-segment-active' : 'text-ff-mute hover:text-ff-ink'}`}
        >
          People
        </button>
      </div>

      {detailQ.isLoading && <p className="text-sm text-ff-mute">Loading…</p>}

      {tab === 'groups' && (
        <div className="ff-card p-5 sm:p-7 space-y-8">
          <div>
            <h3 className="mb-3 font-display text-lg font-bold text-ff-ink">Groups</h3>
            <div className="flex gap-3">
              <input
                data-testid="new-group-name" value={newGroup} placeholder="Group name"
                onChange={(e) => setNewGroup(e.target.value)} className="ff-input flex-1"
              />
              <button
                type="button" data-testid="create-group" onClick={() => createGroup.mutate()}
                disabled={createGroup.isPending || !newGroup.trim()} className="ff-btn-primary px-5"
              >
                {createGroup.isPending ? 'Adding…' : 'Add group'}
              </button>
            </div>
            <div className="mt-4 divide-y divide-ff-sand/60 border-t border-b border-ff-sand/60">
              {groups.length === 0 && <p className="py-4 text-sm text-ff-mute">No groups yet — every store is ungrouped.</p>}
              {groups.map((g) => (
                <GroupRow
                  key={g.id} group={g}
                  onRename={(name) => renameGroup.mutate({ gid: g.id, name })}
                  onDelete={() => deleteGroup.mutate(g.id)}
                />
              ))}
            </div>
          </div>

          <div>
            <h3 className="mb-3 font-display text-lg font-bold text-ff-ink">Stores</h3>
            <div className="divide-y divide-ff-sand/60 border-t border-b border-ff-sand/60">
              {stores.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-4 py-3">
                  <span className="truncate text-sm text-ff-ink/90">{s.name}</span>
                  <select
                    data-testid={`store-group-select-${s.id}`} value={s.group_id ?? ''}
                    onChange={(e) => moveStore.mutate({ storeId: s.id, gid: e.target.value || null })}
                    className="ff-input-sm"
                  >
                    <option value="">Ungrouped</option>
                    {groups.map((g) => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'people' && (
        <div className="ff-card p-5 sm:p-7 space-y-8">
          <div>
            <h3 className="mb-3 font-display text-lg font-bold text-ff-ink">Invite someone</h3>
            <p className="mb-4 text-xs text-ff-mute">
              A member sees the stores of the groups ticked here. An owner sees every store and manages this page.
            </p>
            <div className="grid gap-3 items-end sm:grid-cols-[2fr_1fr]">
              <div>
                <label htmlFor="org-invite-email" className="ff-label">Email</label>
                <input
                  id="org-invite-email" data-testid="invite-email" type="email" value={email}
                  onChange={(e) => setEmail(e.target.value)} placeholder="person@company.com" className="ff-input"
                />
              </div>
              <div>
                <label htmlFor="org-invite-role" className="ff-label">Role</label>
                <select
                  id="org-invite-role" data-testid="invite-role" value={role}
                  onChange={(e) => setRole(e.target.value as OrgRole)} className="ff-input"
                >
                  <option value="member">member</option>
                  <option value="owner">owner</option>
                </select>
              </div>
            </div>
            {groups.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3">
                {groups.map((g) => (
                  <label key={g.id} className="ff-chip cursor-pointer border border-ff-line bg-white normal-case">
                    <input
                      type="checkbox" data-testid={`invite-group-${g.id}`} checked={groupIds.includes(g.id)}
                      onChange={() => setGroupIds(toggleId(groupIds, g.id))} className="mr-1.5"
                    />
                    {g.name}
                  </label>
                ))}
              </div>
            )}
            <button
              type="button" data-testid="invite-submit" onClick={() => invite.mutate()}
              disabled={invite.isPending || !email.trim()} className="ff-btn-primary mt-4 px-5"
            >
              {invite.isPending ? 'Inviting…' : 'Send invite'}
            </button>

            {setPasswordUrl && (
              <div className="mt-5 rounded-xl border border-ff-forest/20 bg-ff-mint/30 p-4">
                <p className="mb-2 text-[11px] font-bold uppercase tracking-widish text-ff-ink-soft">
                  One-time set-password link — copy &amp; send
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="set-password-link" readOnly value={setPasswordUrl}
                    className="ff-input font-data text-xs flex-1"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <button
                    type="button" className="ff-btn-ghost px-4"
                    onClick={() => {
                      navigator.clipboard?.writeText(setPasswordUrl).then(
                        () => setToast({ kind: 'success', msg: 'Link copied.' }),
                        () => setToast({ kind: 'error', msg: 'Copy failed — select and copy manually.' }),
                      );
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
          </div>

          <div>
            <h3 className="mb-3 font-display text-lg font-bold text-ff-ink">People</h3>
            <div className="divide-y divide-ff-sand/60 border-t border-b border-ff-sand/60">
              {users.map((u) => (
                <div
                  key={u.id} data-testid={`user-row-${u.id}`}
                  className="flex flex-wrap items-center justify-between gap-3 py-4"
                >
                  <div className="min-w-0">
                    <p className="truncate text-ff-ink/90">{u.email}</p>
                    {u.disabled && (
                      <p className="text-[11px] font-semibold uppercase tracking-widish text-ff-mute">disabled</p>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <select
                      data-testid={`user-role-${u.id}`} value={u.org_role}
                      onChange={(e) => updateUser.mutate({ uid: u.id, body: { org_role: e.target.value as OrgRole } })}
                      className="ff-input-sm"
                    >
                      <option value="member">member</option>
                      <option value="owner">owner</option>
                    </select>
                    {groups.map((g) => (
                      <label key={g.id} className="ff-chip cursor-pointer border border-ff-line bg-white normal-case">
                        <input
                          type="checkbox" data-testid={`user-group-${u.id}-${g.id}`}
                          checked={u.group_ids.includes(g.id)}
                          onChange={() => updateUser.mutate({ uid: u.id, body: { group_ids: toggleId(u.group_ids, g.id) } })}
                          className="mr-1.5"
                        />
                        {g.name}
                      </label>
                    ))}
                    <button
                      type="button" data-testid={`toggle-active-${u.id}`}
                      onClick={() => toggleActive.mutate({ uid: u.id, active: u.disabled })}
                      className="ff-btn-ghost px-4 py-2 text-xs"
                    >
                      {u.disabled ? 'Reactivate' : 'Deactivate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} onDismiss={() => setToast(null)} />
    </div>
  );
}
